type AuthEventType = "auth:unauthorized" | "auth:forbidden" | "auth:refresh";

type AuthEventData = {
    "auth:unauthorized": { reason?: string; url?: string };
    "auth:forbidden": { resource?: string; url?: string };
    "auth:refresh": { token: string };
};

type AuthEventListener<T extends AuthEventType> = (
    data: AuthEventData[T]
) => void;

class AuthEventEmitter {
    private listeners: Map<AuthEventType, Set<AuthEventListener<any>>> = new Map();

    on<T extends AuthEventType>(event: T, listener: AuthEventListener<T>): () => void {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, new Set());
        }
        this.listeners.get(event)!.add(listener);

        // Return unsubscribe function
        return () => {
            this.listeners.get(event)?.delete(listener);
        };
    }

    emit<T extends AuthEventType>(event: T, data: AuthEventData[T]): void {
        const eventListeners = this.listeners.get(event);
        if (!eventListeners) return;

        eventListeners.forEach((listener) => {
            try {
                listener(data);
            } catch (error) {
                console.error(`Error in auth event listener for ${event}:`, error);
            }
        });
    }

    removeAllListeners(event?: AuthEventType): void {
        if (event) {
            this.listeners.delete(event);
        } else {
            this.listeners.clear();
        }
    }
}

export const authEvents = new AuthEventEmitter();
